import { getTemplateTokenRegex, normalizeString } from '@rentguru/commons-utils';
import type { JSONContent } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import { PARAGRAPH_NODE_NAME } from '@ui/components/editor/richTextUtils';

export const TEMPLATE_TAG_NODE_NAME = 'templateTag';
export const TEMPLATE_TAG_TOKEN_ATTRIBUTE = 'data-template-tag';
export const TEMPLATE_TAG_WRAPPER_ELEMENT = 'span';
export const TEMPLATE_TAG_WRAPPER_SELECTOR = `${TEMPLATE_TAG_WRAPPER_ELEMENT}[${TEMPLATE_TAG_TOKEN_ATTRIBUTE}]`;
export const TEMPLATE_TAG_SUGGESTION_CHAR = '{{';
export const TEMPLATE_TAG_LEAF_TEXT = '\ufffc';
export const LINE_BREAK_TAG = 'br';
export const SINGLE_LINE_SEPARATOR = ' ';

const TEXT_NODE_NAME = 'text';
const DOCUMENT_NODE_NAME = 'doc';
const NEW_LINES_REGEX = /[\r\n]+/g;

export const COMPLETE_TEMPLATE_TOKEN_BEFORE_CARET_REGEX = new RegExp(`${getTemplateTokenRegex().source}$`);

export interface TemplateTagOption {
  token: string;
  label: string;
  category?: string;
}

export interface TemplateTagGroup {
  category?: string;
  tags: TemplateTagOption[];
}

type TemplateTextSegment = { token: string } | { text: string };

const splitTemplateText = (text: string): TemplateTextSegment[] => {
  const { segments, lastIndex } = Array.from(text.matchAll(getTemplateTokenRegex())).reduce<{
    segments: TemplateTextSegment[];
    lastIndex: number;
  }>(
    (acc, match) => {
      const [token] = match;
      const index = match.index ?? acc.lastIndex;
      const before = text.slice(acc.lastIndex, index);
      return {
        segments: [...acc.segments, ...(isEmpty(before) ? [] : [{ text: before }]), { token }],
        lastIndex: index + token.length,
      };
    },
    { segments: [], lastIndex: 0 }
  );
  const rest = text.slice(lastIndex);
  return isEmpty(rest) ? segments : [...segments, { text: rest }];
};

const isTemplateTagWrapper = (node: Node): boolean =>
  node instanceof Element && node.matches(TEMPLATE_TAG_WRAPPER_SELECTOR);

const collectTextNodes = (node: Node): Text[] => {
  if (node instanceof Text) {
    return [node];
  }
  if (isTemplateTagWrapper(node)) {
    return [];
  }
  return Array.from(node.childNodes).flatMap(collectTextNodes);
};

const buildTagWrapper = (ownerDocument: Document, token: string): HTMLElement => {
  const wrapper = ownerDocument.createElement(TEMPLATE_TAG_WRAPPER_ELEMENT);
  wrapper.setAttribute(TEMPLATE_TAG_TOKEN_ATTRIBUTE, token);
  wrapper.textContent = token;
  return wrapper;
};

const wrapTokensOfTextNode = (textNode: Text) => {
  const segments = splitTemplateText(textNode.data);
  if (!segments.some((segment) => 'token' in segment)) {
    return;
  }
  const { ownerDocument } = textNode;
  textNode.replaceWith(
    ...segments.map((segment) =>
      'token' in segment ? buildTagWrapper(ownerDocument, segment.token) : ownerDocument.createTextNode(segment.text)
    )
  );
};

export const transformHtml = (html: string, transform: (content: DocumentFragment) => void): string => {
  const template = document.createElement('template');
  template.innerHTML = html;
  transform(template.content);
  return template.innerHTML;
};

export const wrapTemplateTokens = (html: string): string =>
  transformHtml(html, (content) => collectTextNodes(content).forEach(wrapTokensOfTextNode));

export const replaceLineBreaksWithSpaces = (html: string): string =>
  transformHtml(html, (content) =>
    content.querySelectorAll(LINE_BREAK_TAG).forEach((lineBreak) => {
      lineBreak.replaceWith(SINGLE_LINE_SEPARATOR);
    })
  );

export const flattenToSingleLine = (text: string): string => text.replace(NEW_LINES_REGEX, SINGLE_LINE_SEPARATOR);

const toTemplateInlineContent = (text: string): JSONContent[] =>
  splitTemplateText(text).map((segment) =>
    'token' in segment
      ? { type: TEMPLATE_TAG_NODE_NAME, attrs: { token: segment.token } }
      : { type: TEXT_NODE_NAME, text: segment.text }
  );

export const toSingleLineTemplateDocument = (text: string): JSONContent => ({
  type: DOCUMENT_NODE_NAME,
  content: [{ type: PARAGRAPH_NODE_NAME, content: toTemplateInlineContent(flattenToSingleLine(text)) }],
});

export const groupTemplateTags = (tags: TemplateTagOption[]): TemplateTagGroup[] =>
  tags.reduce<TemplateTagGroup[]>((groups, tag) => {
    const group = groups.find((candidate) => candidate.category === tag.category);
    if (isNil(group)) {
      return [...groups, { category: tag.category, tags: [tag] }];
    }
    return groups.map((candidate) => (candidate === group ? { ...group, tags: [...group.tags, tag] } : candidate));
  }, []);

export const isEveryTokenAvailable = (): boolean => true;

const toSearchableText = (text: string): string => normalizeString(text).toLowerCase();

export const findSuggestedTemplateTags = (
  tags: TemplateTagOption[],
  query: string,
  isTokenAvailable: (token: string) => boolean
): TemplateTagOption[] => {
  const searchedText = toSearchableText(query.trim());
  const matchingTags = tags.filter(
    (tag) =>
      isTokenAvailable(tag.token) &&
      (toSearchableText(tag.label).includes(searchedText) || toSearchableText(tag.token).includes(searchedText))
  );
  return groupTemplateTags(matchingTags).flatMap((group) => group.tags);
};

export const readTemplateTagToken = (node: ProseMirrorNode): string => {
  const { token } = node.attrs;
  return isString(token) ? token : '';
};

export const readTemplateTagLabel = (tags: TemplateTagOption[], token: string): string =>
  tags.find((tag) => tag.token === token)?.label ?? token;
